import type { ReactNode } from 'react';
import {
    Box,
    Skeleton,
    Typography,
    type SxProps,
    type Theme,
} from '@mui/material';
import {
    changeArrow,
    changeColor,
    formatAbsolute,
    formatChange,
    spanPhrase,
    type Change,
} from '../event/chart/changeOverRange';

// "27 % ▲ +6.5 pts in 1D" and "$123.45 ▲ +2.3% · +$2.78 in 1D": a value, and
// what it did over the shown range, in the units the design settled on
// (changeOverRange). The value node brings its own formatting; give it
// BIG_VALUE_SX for the size: 40 px on phones, 24 px from md.

export const BIG_VALUE_SX = {
    fontSize: { xs: '40px', md: '24px' },
    fontWeight: 700,
    letterSpacing: '-0.02em',
    lineHeight: 1.1,
    fontVariantNumeric: 'tabular-nums',
} as const;

interface BigNumberProps {
    value: ReactNode;
    change: Change | null;
    /** "1D", "All", "5d". */
    rangeLabel: string;
    /**
     * The change is on its way: placeholders hold its place (and the width a
     * row of figures wraps at) until it comes.
     */
    changePending?: boolean;
    sx?: SxProps<Theme>;
}

const CHANGE_SX = { fontSize: { xs: '16px', md: '14px' }, fontWeight: 700 };
const DETAIL_SX = { fontSize: { xs: '14px', md: '12px' } };

export function BigNumber({
    value,
    change,
    rangeLabel,
    changePending = false,
    sx,
}: BigNumberProps) {
    const absolute = change ? formatAbsolute(change) : null;
    return (
        <Box
            data-testid="big-number"
            sx={[
                {
                    display: 'flex',
                    alignItems: 'baseline',
                    flexWrap: 'wrap',
                    columnGap: 1.25,
                    minWidth: 0,
                },
                ...(Array.isArray(sx) ? sx : sx ? [sx] : []),
            ]}
        >
            {value}
            {!change && changePending && (
                <>
                    <Skeleton
                        data-testid="big-number-change-placeholder"
                        sx={{ display: 'flex' }}
                    >
                        <Typography component="span" sx={CHANGE_SX}>
                            ▲ +5.6 pts
                        </Typography>
                    </Skeleton>
                    <Skeleton sx={{ display: 'flex' }}>
                        <Typography component="span" sx={DETAIL_SX}>
                            {`in ${spanPhrase(rangeLabel)}`}
                        </Typography>
                    </Skeleton>
                </>
            )}
            {change && (
                <>
                    <Typography
                        component="span"
                        data-testid="big-number-change"
                        sx={{ ...CHANGE_SX, color: changeColor(change) }}
                    >
                        {[changeArrow(change), formatChange(change)]
                            .filter(Boolean)
                            .join(' ')}
                    </Typography>
                    <Typography
                        component="span"
                        data-testid="big-number-detail"
                        sx={{ ...DETAIL_SX, color: 'text.secondary' }}
                    >
                        {[
                            absolute ? `· ${absolute}` : null,
                            `in ${spanPhrase(rangeLabel)}`,
                        ]
                            .filter(Boolean)
                            .join(' ')}
                    </Typography>
                </>
            )}
        </Box>
    );
}
